import { env } from "../../../config/env";
import { authStorage } from "../../../lib/storage/authStorage";
import { fetchFlyerCategoryOptions, serializeTemplateFields } from "../common/flyerTemplateDevService";
import type { FlyerCategoryOption, TemplateFieldDef } from "../common/flyerTemplateDevService";
import type { VideoTemplateItem } from "../common/templateTypes";

export { fetchFlyerCategoryOptions };
export type { FlyerCategoryOption, TemplateFieldDef };
export type { VideoTemplateItem };

/**
 * Init shape used by the video dev form.
 * Mirrors VideoTemplateItem from the backend — all fields optional except id, name, category.
 */
export type VideoTemplateInit = Partial<VideoTemplateItem> & Pick<VideoTemplateItem, "id" | "name" | "category">;

function authHeaders(): HeadersInit {
    const token = authStorage.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface CreateVideoTemplateInput {
    category_slug: string;
    name: string;
    base_prompt?: string;
    fields: TemplateFieldDef[];
    /** Video file (mp4/mov/webm) — backend auto-extracts first frame as thumbnail */
    template_video: File;
    cta_text?: string;
    tagline?: string;
    mood?: string;
    aspect_ratio?: string;
    prompt_template?: string;
    camera_movement?: string;
    motion_style?: string;
    transition_style?: string;
    text_animation_style?: string;
    audio_mood?: string;
    scene_structure?: string;
    /** Clip length in seconds — Kling supports 5 or 10 (default 10) */
    video_duration?: 5 | 10;
    /** Hex color for text/CTA overlays — only used when USE_VIDEO_V2 renders via the videoagent platform */
    brand_color?: string;
    /** Must match remotion_renderer's known fonts (Inter, Montserrat, Playfair Display, Poppins, Cormorant Garamond) */
    font_family?: string;
    /** minimal | bold | elegant */
    overlay_style?: string;
    /** fade | slideUp | pop | none | letterReveal | wordPunch */
    overlay_animation?: string;
    /** underline | gradientShimmer | none */
    overlay_decoration?: string;
}

export type UpdateVideoTemplateInput = Omit<CreateVideoTemplateInput, "template_video"> & {
    /** Replacement video file — optional on update */
    template_video?: File;
};

function buildVideoFormData(input: UpdateVideoTemplateInput, includeVideo?: File): FormData {
    const form = new FormData();
    form.append("category_slug", input.category_slug);
    form.append("name", input.name);
    form.append("base_prompt", input.base_prompt ?? "");
    form.append("fields_json", serializeTemplateFields(input.fields));
    if (includeVideo) form.append("template_video", includeVideo);
    form.append("cta_text", input.cta_text ?? "BOOK NOW");
    form.append("tagline", input.tagline ?? "");
    form.append("mood", input.mood ?? "");
    form.append("aspect_ratio", input.aspect_ratio ?? "9:16");
    form.append("prompt_template", input.prompt_template ?? "");
    form.append("camera_movement", input.camera_movement ?? "");
    form.append("motion_style", input.motion_style ?? "");
    form.append("transition_style", input.transition_style ?? "");
    form.append("text_animation_style", input.text_animation_style ?? "");
    form.append("audio_mood", input.audio_mood ?? "");
    form.append("scene_structure", input.scene_structure ?? "");
    form.append("video_duration", String(input.video_duration ?? 10));
    form.append("brand_color", input.brand_color ?? "");
    form.append("font_family", input.font_family ?? "");
    form.append("overlay_style", input.overlay_style ?? "");
    form.append("overlay_animation", input.overlay_animation ?? "");
    form.append("overlay_decoration", input.overlay_decoration ?? "");
    return form;
}

export async function createVideoTemplateDev(input: CreateVideoTemplateInput): Promise<void> {
    const form = buildVideoFormData(input, input.template_video);
    const res = await fetch(`${env.apiBaseUrl}/rag/video-templates`, {
        method: "POST",
        headers: authHeaders(),
        body: form,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = (err as { detail?: string }).detail ?? res.statusText;
        throw new Error(typeof detail === "string" ? detail : "Create failed");
    }
}

export interface PlanVideoTemplatePreviewResult {
    timeline: { scenes: Array<{ id: string; scene_prompt: string; [key: string]: unknown }> };
}

export interface StartVideoTemplatePreviewResult {
    preview_id: string;
    status: string;
}

export interface VideoTemplatePreviewStatusResult {
    preview_id: string;
    status: string; // "pending" | "completed" | "failed"
    output_url: string;
    job_id: string;
    scenes: unknown[];
    video_s3_key: string;
    video_url: string;
    preview_s3_key: string;
    preview_url: string;
    error: string;
}

/** Step 1 of admin "Generate Preview": sends a prompt, gets back the
 * Director's scene plan (no footage generated, no spend yet) so the admin
 * can review the revised/refined prompt per scene before confirming. */
export async function planVideoTemplatePreview(
    prompt: string,
    opts?: { aspect_ratio?: string; duration_hint_seconds?: number },
): Promise<PlanVideoTemplatePreviewResult> {
    const res = await fetch(`${env.apiBaseUrl}/rag/video-templates/plan-preview`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
            prompt,
            aspect_ratio: opts?.aspect_ratio,
            duration_hint_seconds: opts?.duration_hint_seconds,
        }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = (err as { detail?: string }).detail ?? res.statusText;
        throw new Error(typeof detail === "string" ? detail : "Preview planning failed");
    }
    return res.json() as Promise<PlanVideoTemplatePreviewResult>;
}

/** Step 2, submit half: kicks off real footage sourcing for an approved
 * timeline (from planVideoTemplatePreview above) as a background job and
 * returns immediately with a preview_id — real generation can take several
 * minutes, longer than a single request should be held open for. Poll
 * getVideoTemplatePreviewStatus(preview_id) for the result. */
export async function startVideoTemplatePreview(
    timeline: PlanVideoTemplatePreviewResult["timeline"],
): Promise<StartVideoTemplatePreviewResult> {
    const res = await fetch(`${env.apiBaseUrl}/rag/video-templates/generate-preview`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ timeline }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = (err as { detail?: string }).detail ?? res.statusText;
        throw new Error(typeof detail === "string" ? detail : "Preview generation failed");
    }
    return res.json() as Promise<StartVideoTemplatePreviewResult>;
}

/** Step 2, poll half — see startVideoTemplatePreview above. */
export async function getVideoTemplatePreviewStatus(previewId: string): Promise<VideoTemplatePreviewStatusResult> {
    const res = await fetch(`${env.apiBaseUrl}/rag/video-templates/generate-preview/${encodeURIComponent(previewId)}`, {
        method: "GET",
        headers: authHeaders(),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = (err as { detail?: string }).detail ?? res.statusText;
        throw new Error(typeof detail === "string" ? detail : "Preview status check failed");
    }
    return res.json() as Promise<VideoTemplatePreviewStatusResult>;
}

export async function updateVideoTemplateDev(
    templateId: string,
    input: UpdateVideoTemplateInput,
): Promise<void> {
    const form = buildVideoFormData(input, input.template_video);
    const res = await fetch(`${env.apiBaseUrl}/rag/video-templates/${encodeURIComponent(templateId)}`, {
        method: "PUT",
        headers: authHeaders(),
        body: form,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = (err as { detail?: string }).detail ?? res.statusText;
        throw new Error(typeof detail === "string" ? detail : "Update failed");
    }
}
